import DOMPurify from 'dompurify';
import { env } from '../config/env';
import { slugify } from './product';

export const BLOG_MEDIA_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';
export const BLOG_MEDIA_MAX_BYTES = 5 * 1024 * 1024;

const YOUTUBE_EMBED_PATTERN =
  /^https:\/\/(www\.)?(youtube-nocookie\.com\/embed\/|youtube\.com\/embed\/)[\w-]+(\?[\w=&%-]*)?$/;

function deriveApiOrigin(url: string): string {
  if (!url) return '';
  return url.replace(/\/api\/?$/, '');
}

const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*>/i;

export function isBlogHtmlContent(content: string): boolean {
  return HTML_TAG_PATTERN.test(content.trim());
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function plainTextToEditorHtml(content: string): string {
  const trimmed = content.trim();
  if (!trimmed) return '';

  return trimmed
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

export function normalizeEditorHtml(html: string): string {
  const trimmed = html.trim();
  if (!trimmed || trimmed === '<p></p>') return '';
  return trimmed;
}

export function stripBlogHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}

export function extractFirstBlogImageSrc(html: string): string | null {
  if (!html.trim()) return null;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const img = doc.querySelector('img[src]');
  const src = img?.getAttribute('src')?.trim();
  return src || null;
}

export function getBlogPostThumbnail(post: {
  coverImage?: string | null;
  content: string;
}): string | null {
  const cover = post.coverImage?.trim();
  if (cover) return resolveBlogMediaUrl(cover);

  const fromContent = extractFirstBlogImageSrc(post.content);
  if (fromContent) return resolveBlogMediaUrl(fromContent);

  return null;
}

export function hasBlogContent(html: string): boolean {
  const normalized = normalizeEditorHtml(html);
  if (!normalized) return false;
  if (stripBlogHtml(normalized)) return true;

  const doc = new DOMParser().parseFromString(normalized, 'text/html');
  return Boolean(doc.querySelector('img, iframe, hr'));
}

export function resolveBlogMediaUrl(url: string): string {
  if (!url) return '';
  if (
    url.startsWith('http://') ||
    url.startsWith('https://') ||
    url.startsWith('blob:') ||
    url.startsWith('data:')
  ) {
    return url;
  }

  if (url.startsWith('/')) {
    const origin = env.apiOrigin || deriveApiOrigin(env.apiUrl);
    return origin ? `${origin}${url}` : url;
  }

  return url;
}

export function validateBlogMediaFile(file: File): string | null {
  if (!BLOG_MEDIA_ACCEPT.split(',').includes(file.type)) {
    return `${file.name}: Yalnızca PNG, JPG, WEBP veya GIF yüklenebilir.`;
  }
  if (file.size > BLOG_MEDIA_MAX_BYTES) {
    return `${file.name}: Dosya boyutu 5 MB'ı aşamaz.`;
  }
  return null;
}

function resolveHtmlMediaUrls(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('img[src]').forEach((img) => {
    const src = img.getAttribute('src');
    if (src) {
      img.setAttribute('src', resolveBlogMediaUrl(src));
    }
  });
  return doc.body.innerHTML;
}

export function getBlogContentHtml(content: string): string {
  if (!content.trim()) return '';

  const html = isBlogHtmlContent(content) ? content : plainTextToEditorHtml(content);
  return resolveHtmlMediaUrls(sanitizeBlogHtml(html));
}

let blogSanitizeHookReady = false;

function ensureBlogSanitizeHooks() {
  if (blogSanitizeHookReady) return;

  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName === 'iframe' && node instanceof Element) {
      const src = node.getAttribute('src') ?? '';
      if (!YOUTUBE_EMBED_PATTERN.test(src)) {
        node.remove();
      }
    }
  });

  blogSanitizeHookReady = true;
}

export function sanitizeBlogHtml(html: string): string {
  ensureBlogSanitizeHooks();

  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'b',
      'em',
      'i',
      'u',
      's',
      'h2',
      'h3',
      'ul',
      'ol',
      'li',
      'blockquote',
      'a',
      'hr',
      'img',
      'iframe',
      'div',
    ],
    ALLOWED_ATTR: [
      'href',
      'target',
      'rel',
      'src',
      'alt',
      'title',
      'width',
      'height',
      'loading',
      'class',
      'allow',
      'allowfullscreen',
      'frameborder',
      'referrerpolicy',
    ],
  });

  return sanitized;
}

export function parseBlogPostId(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function formatBlogDate(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function formatBlogDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function resolveBlogSlug(title: string, slug?: string | null): string {
  const trimmed = slug?.trim();
  if (trimmed) return trimmed;
  return slugify(title) || 'blog-yazisi';
}

export function renderBlogContent(content: string): string[] {
  return content
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

export function toDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60_000);
  return local.toISOString().slice(0, 16);
}

export function fromDatetimeLocalValue(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}
